/**
 * El detalle de un envío: cuántos se generaron, cuántos firmaron y, por
 * persona, en qué está o por qué no se le generó.
 *
 * Mientras se genera, la pantalla se actualiza sola (cada 2 s) y deja de
 * preguntar al terminar. Cada persona enlaza a su ficha, que es donde se corrige
 * un correo o un dato que faltaba.
 *
 * Ruta: `/payroll/documents/batches/:id`.
 */
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { ProgressTrack } from '@/components/ds/ProgressTrack'
import { formatDate, useTenantLocale } from '@/lib/locale'
import { useEnvio, type ItemDelEnvio } from '../../hooks/usePlantillasDocumentos'
import { ESTADOS_ENVIO } from './utilidades'

const FIRMAS = {
  pending: { label: 'Pendiente de firma', tone: 'warning' },
  signed: { label: 'Firmado', tone: 'success' },
  rejected: { label: 'Rechazado', tone: 'danger' },
  cancelled: { label: 'Anulado', tone: 'neutral' },
} as const

export default function EnvioDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const locale = useTenantLocale()
  const { data: envio, isLoading } = useEnvio(id)

  if (isLoading || !envio) {
    return <div className="text-sm text-content-muted p-6">Cargando el envío…</div>
  }
  const generando = envio.status === 'queued' || envio.status === 'running'
  const s = envio.signatures

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => navigate('/payroll/documents?tab=envios')} aria-label="Volver a los envíos"
          className="p-1.5 rounded-lg hover:bg-soft text-content-muted"><ArrowLeft size={18} /></button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-content truncate">{envio.name}</h2>
          <p className="text-xs text-content-muted">
            {formatDate(envio.created_at, locale)}{envio.created_by_name ? ` · ${envio.created_by_name}` : ''} · plantilla «{envio.template_name}»
          </p>
        </div>
        <StatusBadge status={envio.status} tone={ESTADOS_ENVIO[envio.status].tone}
          label={ESTADOS_ENVIO[envio.status].label} live={generando} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-divider bg-surface p-4">
          <ProgressTrack label="Documentos generados" value={envio.generated} max={envio.total} showValue
            hint={envio.failed > 0 ? `${envio.failed} no se ${envio.failed === 1 ? 'pudo' : 'pudieron'} generar: el motivo está en la tabla.` : undefined} />
        </div>
        {envio.requires_signature && (
          <div className="rounded-xl border border-divider bg-surface p-4">
            <ProgressTrack label="Firmados" value={s.signed} max={Math.max(envio.generated, 1)} showValue
              hint={[s.pending && `${s.pending} pendientes`, s.rejected && `${s.rejected} rechazados`]
                .filter(Boolean).join(' · ') || undefined} />
          </div>
        )}
      </div>

      <DataTable<ItemDelEnvio>
        rows={envio.items}
        rowKey={(i) => i.id}
        columns={[
          {
            key: 'name', header: 'Trabajador', sortable: true, sortValue: (i) => i.employee_name.toLowerCase(),
            render: (i) => (
              <div>
                <Link to={`/payroll/employees?ficha=${i.employee}`} className="text-content hover:underline">{i.employee_name}</Link>
                <div className="text-xs text-content-muted">{i.employee_rut}</div>
              </div>
            ),
          },
          {
            key: 'status', header: 'Documento',
            render: (i) => i.status === 'generated'
              ? <StatusBadge status="ok" tone="success" label="Generado" />
              : i.status === 'pending'
                ? <StatusBadge status="pending" tone="neutral" label="En cola" live={generando} />
                : <span className="text-sm text-danger-content">{i.error_message || 'No se pudo generar.'}</span>,
          },
          ...(envio.requires_signature ? [{
            key: 'firma', header: 'Firma',
            render: (i: ItemDelEnvio) => i.signature_status
              ? <StatusBadge status={i.signature_status} tone={FIRMAS[i.signature_status].tone}
                label={FIRMAS[i.signature_status].label} />
              : <span className="text-content-subtle">—</span>,
          }] : []),
        ]}
      />
    </div>
  )
}
